/**
 * Servicio para integración con Groq API
 */

import AIProviderService from './aiProviderService';
import { apiUrl, getApiBase } from '../utils/apiUrl';

class GroqService extends AIProviderService {
  constructor() {
    super();
    this.apiKey = null; // Will be loaded from server
    this.model = 'llama-3.3-70b-versatile'; // Default, will be updated
    this.baseURL = 'https://api.groq.com/openai/v1';
    this.configLoaded = false;
  }

  /**
   * Carga la configuración desde el servidor
   * @returns {Promise<void>}
   */
  async loadConfig() {
    // Si ya tenemos configuración pasada desde el factory, usarla
    if (this.config && this.configLoaded) {
      this.apiKey = this.config.groqApiKey;
      const raw = this.config.groqModel || 'llama-3.3-70b-versatile';
      this.model = raw === 'llama-3.1-70b-versatile' ? 'llama-3.3-70b-versatile' : raw;
      this.log('Usando configuración pasada desde factory', {
        provider: this.config.aiProvider,
        model: this.model,
        hasApiKey: !!this.apiKey
      });
      return;
    }

    // Si no, cargar desde el servidor (fallback)
    try {
      const response = await fetch(apiUrl('/api/config'), { credentials: 'include' });
      if (response.ok) {
        const config = await response.json();
        this.apiKey = config.groqApiKey;
        const rawModel = config.groqModel || 'llama-3.3-70b-versatile';
        this.model = rawModel === 'llama-3.1-70b-versatile' ? 'llama-3.3-70b-versatile' : rawModel;
        this.configLoaded = true;
        
        this.log('Configuración cargada desde servidor (fallback)', {
          provider: config.aiProvider,
          model: this.model,
          hasApiKey: !!this.apiKey
        });
      } else {
        this.log('Error cargando configuración desde servidor', { status: response.status }, 'warn');
      }
    } catch (error) {
      this.log('Error cargando configuración desde servidor', { error: error.message }, 'error');
    }
  }

  /**
   * Configura el servicio con datos pasados desde el factory
   * @param {Object} config - Configuración del servidor
   */
  setConfig(config) {
    this.config = config;
    this.apiKey = config.groqApiKey;
    // Modelo 3.1 está descontinuado en Groq; forzar 3.3 si viene el antiguo
    const raw = config.groqModel || 'llama-3.3-70b-versatile';
    this.model = raw === 'llama-3.1-70b-versatile' ? 'llama-3.3-70b-versatile' : raw;
    this.configLoaded = true;
    
    this.log('Configuración establecida desde factory', {
      provider: config.aiProvider,
      model: this.model,
      hasApiKey: !!this.apiKey
    });
  }

  /**
   * Verifica si Groq está disponible
   * @returns {Promise<boolean>} True si está disponible
   */
  async isAvailable() {
    try {
      // Cargar configuración si no está cargada
      if (!this.configLoaded) {
        await this.loadConfig();
      }

      this.log('Verificando disponibilidad de Groq', {
        model: this.model,
        hasApiKey: !!this.apiKey
      });

      if (!this.apiKey) {
        this.log('API key de Groq no configurada', {}, 'warn');
        return false;
      }

      const response = await fetch(`${this.baseURL}/models`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429) {
        this.log('Groq no disponible: límite 429 en /models', {}, 'warn');
        console.warn('[MisKosas Groq] isAvailable=false — rate limit (429)');
        return false;
      }
      if (response.status === 402) {
        this.log('Groq no disponible: pago requerido (402)', {}, 'warn');
        console.warn('[MisKosas Groq] isAvailable=false — payment required (402)');
        return false;
      }

      if (response.ok) {
        const data = await response.json();
        const remaining = response.headers.get('x-ratelimit-remaining-requests');
        this.log('Groq disponible (/models OK)', {
          models: data.data?.length || 0,
          rateLimitRemaining: remaining
        });
        console.warn('[MisKosas Groq] isAvailable=true (/models). La cuota de chat se comprueba al generar en el servidor.');
        return true;
      }

      this.log('Groq no disponible', {
        status: response.status,
        statusText: response.statusText
      }, 'warn');
      return false;
    } catch (error) {
      this.log('Error verificando disponibilidad de Groq', { 
        error: error.message
      }, 'error');
      return false;
    }
  }

  /**
   * Genera recomendaciones usando Groq
   * @param {Object} userData - Datos del usuario
   * @param {string} contentType - Tipo de contenido (movie, series, music)
   * @param {number} count - Número de recomendaciones
   * @returns {Promise<Array>} Recomendaciones generadas
   */
  async generateRecommendations(userData, contentType = 'movie', count = 20) {
    try {
      // Cargar configuración si no está cargada
      if (!this.configLoaded) {
        await this.loadConfig();
      }

      this.log('Iniciando generación de recomendaciones con Groq', {
        contentType,
        count,
        model: this.model,
        temperature: this.temperature,
        maxTokens: this.maxTokens,
        userDataKeys: Object.keys(userData)
      });

      const language = userData.language || 'es';
      const prompt = this.buildUnifiedPrompt(userData, contentType, count, language);
      
      this.log('Prompt construido para Groq', {
        contentType,
        promptLength: prompt.length,
        promptPreview: prompt.substring(0, 200) + '...'
      });
      
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: this.temperature,
          max_tokens: this.maxTokens,
          response_format: { type: "json_object" }
        })
      });

      if (!response.ok) {
        if (response.status === 429) {
          const retryAfter = response.headers.get('retry-after') || '60';
          this.log('Límite de velocidad de Groq excedido (429)', {
            contentType,
            retryAfter
          }, 'warn');
          const error = new Error('GROQ_LIMIT_REACHED');
          error.groqError = {
            type: 'rate_limit',
            message: 'Groq alcanzó su límite de requests hoy',
            retryAfter: parseInt(retryAfter, 10),
            status: 429
          };
          throw error;
        }
        if (response.status === 402) {
          const error = new Error('GROQ_PAYMENT_REQUIRED');
          error.groqError = {
            type: 'payment_required',
            message: 'Groq requiere pago. Tu plan gratuito se agotó.',
            status: 402
          };
          throw error;
        }
        const error = new Error(`Groq API error: ${response.status} ${response.statusText}`);
        error.groqError = { type: 'api_error', status: response.status, message: response.statusText };
        throw error;
      }

      const data = await response.json();
      this.log('Respuesta recibida de Groq', {
        contentType,
        usage: data.usage,
        model: data.model
      });

      const content = data.choices[0]?.message?.content;
      if (!content) {
        throw new Error('No se recibió contenido de Groq');
      }

      // Parsear JSON de la respuesta
      let recommendations = [];
      try {
        const parsed = JSON.parse(content);
        recommendations = Array.isArray(parsed.recommendations) ? parsed.recommendations : (parsed.recommendations ? [parsed.recommendations] : []);
      } catch (parseError) {
        this.log('Error parseando JSON de Groq', { 
          content: content.substring(0, 500),
          error: parseError.message 
        }, 'error');
        
        // Intentar extraer JSON del texto
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            const parsed = JSON.parse(jsonMatch[0]);
            recommendations = Array.isArray(parsed.recommendations) ? parsed.recommendations : (parsed.recommendations ? [parsed.recommendations] : []);
          } catch (secondParseError) {
            throw new Error('No se pudo parsear la respuesta JSON de Groq');
          }
        } else {
          throw new Error('No se encontró JSON válido en la respuesta de Groq');
        }
      }

      // Asegurar que recommendations sea un array
      if (!Array.isArray(recommendations)) {
        this.log('Recomendaciones no es un array, convirtiendo', { recommendations }, 'warn');
        recommendations = recommendations ? [recommendations] : [];
      }

      // Procesar y enriquecer las recomendaciones
      if (!Array.isArray(recommendations) || recommendations.length === 0) {
        this.log('Advertencia: recommendations no es un array o está vacío antes de procesar', {
          contentType,
          recommendationsType: typeof recommendations,
          isArray: Array.isArray(recommendations),
          length: Array.isArray(recommendations) ? recommendations.length : 'N/A'
        }, 'warn');
        return [];
      }

      const processedRecommendations = recommendations.map((rec, index) => {
        // Calcular calificaciones más inteligentes
        const externalRating = this.calculateExternalRating(rec);
        const aiRating = this.calculateAIRatingFromRecommendation(rec, userData);
        const averageRating = (externalRating + aiRating) / 2;

        return {
          id: `groq_${contentType}_${Date.now()}_${index}`,
          title: rec.title || 'Título no disponible',
          year: rec.year || new Date().getFullYear(),
          genre: Array.isArray(rec.genre) ? rec.genre : [rec.genre || 'No especificado'],
          ...(contentType === 'music'
            ? { artist: rec.artist || 'No especificado' }
            : {
                director: rec.director || 'No especificado',
                actors: Array.isArray(rec.actors) ? rec.actors : [rec.actors || 'No especificado']
              }),
          external_rating: externalRating,
          ai_rating: aiRating,
          average_rating: averageRating,
          reason: rec.reason || 'Recomendación basada en tus preferencias',
          type: contentType,
          provider: 'groq',
          created_at: new Date().toISOString()
        };
      });

      this.log('Recomendaciones procesadas de Groq', {
        contentType,
        originalCount: recommendations.length,
        processedCount: processedRecommendations.length,
        sampleRecommendation: processedRecommendations[0] || null
      });

      // Verificar que processedRecommendations sea un array válido antes de retornar
      if (!Array.isArray(processedRecommendations)) {
        this.log('Error crítico: processedRecommendations no es un array', {
          contentType,
          type: typeof processedRecommendations,
          value: processedRecommendations
        }, 'error');
        return [];
      }

      this.log('Retornando recomendaciones de GroqService', {
        contentType,
        count: processedRecommendations.length,
        firstItem: processedRecommendations[0] || null
      });

      return processedRecommendations;

    } catch (error) {
      this.log('Error generando recomendaciones con Groq', {
        contentType,
        error: error.message,
        stack: error.stack
      }, 'error');
      
      // Retornar recomendaciones de respaldo
      return this.getFallbackRecommendations(contentType, count);
    }
  }

  /**
   * Calcula calificación de IA para contenido específico usando Groq
   * @param {Object} content - Contenido a analizar
   * @param {Object} userPreferences - Preferencias del usuario
   * @returns {Promise<number>} Calificación de IA
   */
  async calculateAIRating(content, userPreferences) {
    try {
      // Cargar configuración si no está cargada
      if (!this.configLoaded) {
        await this.loadConfig();
      }

      const isMusic = content.type === 'music' || content.artist;
      const prompt = `Analiza este contenido y calcula una calificación del 1 al 10 basándote en:

CONTENIDO:
- Título: ${content.title}
- Género: ${content.genre}
- Año: ${content.year}
${isMusic ? `- Artista: ${content.artist || 'No especificado'}` : `- Actores: ${content.actors?.join(', ') || 'No especificados'}
- Director: ${content.director || 'No especificado'}`}

PREFERENCIAS DEL USUARIO:
- Géneros favoritos: ${userPreferences.favorite_genres?.join(', ') || 'No especificados'}
- ${isMusic ? 'Artistas' : 'Actores'} favoritos: ${userPreferences.favorite_actors?.join(', ') || 'No especificados'}

Responde SOLO con un número del 1 al 10 (ejemplo: 8.5)`;

      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: this.model,
          messages: [
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: this.temperature,
          max_tokens: 50
        })
      });

      if (!response.ok) {
        if (response.status === 429) {
          const retryAfter = response.headers.get('retry-after') || '60';
          this.log('Límite de velocidad de Groq excedido (429) en calculateAIRating', {
            retryAfter
          }, 'warn');
          const error = new Error('GROQ_LIMIT_REACHED');
          error.groqError = {
            type: 'rate_limit',
            message: 'Groq alcanzó su límite de requests hoy',
            retryAfter: parseInt(retryAfter, 10),
            status: 429
          };
          throw error;
        }
        if (response.status === 402) {
          const error = new Error('GROQ_PAYMENT_REQUIRED');
          error.groqError = {
            type: 'payment_required',
            message: 'Groq requiere pago. Tu plan gratuito se agotó.',
            status: 402
          };
          throw error;
        }
        throw new Error(`Groq API error: ${response.status}`);
      }

      const data = await response.json();
      const ratingText = data.choices[0]?.message?.content?.trim();
      
      // Extraer número de la respuesta
      const ratingMatch = ratingText?.match(/(\d+\.?\d*)/);
      const rating = ratingMatch ? parseFloat(ratingMatch[1]) : 7.0;
      
      // Asegurar que esté en el rango 1-10
      return Math.max(1, Math.min(10, rating));

    } catch (error) {
      this.log('Error calculando calificación con Groq', { 
        error: error.message 
      }, 'error');
      return 7.0; // Calificación por defecto
    }
  }

  /**
   * Calcula calificación externa basada en el contenido
   * @param {Object} rec - Recomendación
   * @returns {number} Calificación externa
   */
  calculateExternalRating(rec) {
    // Si ya tiene calificación externa, usarla
    if (rec.external_rating && rec.external_rating > 0) {
      return parseFloat(rec.external_rating);
    }

    // Calcular basado en año (películas más recientes tienden a tener mejor calidad técnica)
    const currentYear = new Date().getFullYear();
    const year = rec.year || currentYear;
    const yearScore = Math.max(5.0, Math.min(9.0, 6.0 + (year - 1990) * 0.05));

    // Ajustar basado en género (algunos géneros tienen mejor reputación)
    const genreBonus = this.getGenreBonus(rec.genre);
    
    return Math.round((yearScore + genreBonus) * 10) / 10;
  }

  /**
   * Obtiene bonus por género
   * @param {Array|string} genre - Género(s)
   * @returns {number} Bonus por género
   */
  getGenreBonus(genre) {
    const genres = Array.isArray(genre) ? genre : [genre];
    const genreBonuses = {
      'Drama': 0.5,
      'Thriller': 0.3,
      'Crime': 0.2,
      'Sci-Fi': 0.4,
      'Action': 0.1,
      'Comedy': -0.1,
      'Romance': -0.2,
      'Horror': 0.0,
      'Documentary': 0.6,
      'Biography': 0.4
    };

    let totalBonus = 0;
    genres.forEach(g => {
      const genreKey = Object.keys(genreBonuses).find(key => 
        g.toLowerCase().includes(key.toLowerCase())
      );
      if (genreKey) {
        totalBonus += genreBonuses[genreKey];
      }
    });

    return Math.min(1.0, Math.max(-0.5, totalBonus));
  }

  /**
   * Calcula calificación de IA basada en la recomendación y preferencias del usuario
   * @param {Object} rec - Recomendación
   * @param {Object} userData - Datos del usuario
   * @returns {number} Calificación de IA
   */
  calculateAIRatingFromRecommendation(rec, userData) {
    // Si ya tiene calificación de IA, usarla
    if (rec.ai_rating && rec.ai_rating > 0) {
      return parseFloat(rec.ai_rating);
    }

    let aiRating = 7.0; // Base

    // Bonus por coincidencia de género
    const userGenres = userData.preferences?.favorite_genres || [];
    const recGenres = Array.isArray(rec.genre) ? rec.genre : [rec.genre];
    
    const genreMatch = recGenres.some(recGenre => 
      userGenres.some(userGenre => 
        recGenre.toLowerCase().includes(userGenre.toLowerCase())
      )
    );
    
    if (genreMatch) {
      aiRating += 1.0;
    }

    // Bonus por coincidencia de actores o artista (música)
    const userActorsArtists = userData.preferences?.favorite_actors || [];
    const actorOrArtistMatch = rec.artist
      ? userActorsArtists.some(fav => (rec.artist || '').toLowerCase().includes((fav || '').toLowerCase()))
      : (Array.isArray(rec.actors) ? rec.actors : [rec.actors]).some(recActor =>
          userActorsArtists.some(userActor =>
            (recActor || '').toLowerCase().includes((userActor || '').toLowerCase())
          )
        );
    if (actorOrArtistMatch) {
      aiRating += 0.8;
    }

    // Bonus por año (preferencias del usuario)
    const userYearRange = userData.preferences?.preferred_years;
    if (userYearRange && rec.year) {
      const year = rec.year;
      if (year >= userYearRange.min && year <= userYearRange.max) {
        aiRating += 0.5;
      }
    }

    // Bonus por director conocido (solo películas/series)
    if (rec.director && !rec.artist) {
      const knownDirectors = [
        'Christopher Nolan', 'Martin Scorsese', 'Steven Spielberg',
        'Quentin Tarantino', 'David Fincher', 'Ridley Scott',
        'Alfred Hitchcock', 'Stanley Kubrick', 'Francis Ford Coppola'
      ];
      if (knownDirectors.some(director =>
        rec.director.toLowerCase().includes(director.toLowerCase())
      )) {
        aiRating += 0.7;
      }
    }

    return Math.round(Math.min(10.0, Math.max(1.0, aiRating)) * 10) / 10;
  }



  /**
   * Obtiene recomendaciones de respaldo si Groq falla
   * @param {string} contentType - Tipo de contenido
   * @param {number} count - Número de recomendaciones
   * @returns {Array} Recomendaciones de respaldo
   */
  getFallbackRecommendations(contentType, count) {
    const fallbackRecommendations = {
      movie: [
        {
          id: `groq_fallback_movie_${Date.now()}`,
          title: 'The Matrix',
          year: 1999,
          genre: ['Sci-Fi', 'Action'],
          director: 'The Wachowskis',
          actors: ['Keanu Reeves', 'Laurence Fishburne'],
          external_rating: 8.7,
          ai_rating: 9.0,
          average_rating: 8.85,
          reason: 'Clásico de ciencia ficción que combina acción y filosofía',
          type: 'movie',
          provider: 'groq_fallback',
          created_at: new Date().toISOString()
        }
      ],
      series: [
        {
          id: `groq_fallback_series_${Date.now()}`,
          title: 'Breaking Bad',
          year: 2008,
          genre: ['Drama', 'Crime'],
          director: 'Vince Gilligan',
          actors: ['Bryan Cranston', 'Aaron Paul'],
          external_rating: 9.5,
          ai_rating: 9.8,
          average_rating: 9.65,
          reason: 'Serie dramática intensa con desarrollo de personajes excepcional',
          type: 'series',
          provider: 'groq_fallback',
          created_at: new Date().toISOString()
        }
      ],
      music: [
        {
          id: `groq_fallback_music_${Date.now()}`,
          title: 'Bohemian Rhapsody',
          year: 1975,
          genre: ['Rock', 'Progressive'],
          artist: 'Queen',
          external_rating: 9.2,
          ai_rating: 9.5,
          average_rating: 9.35,
          reason: 'Obra maestra del rock con múltiples estilos musicales',
          type: 'music',
          provider: 'groq_fallback',
          created_at: new Date().toISOString()
        }
      ]
    };

    return fallbackRecommendations[contentType] || [];
  }
}

export default GroqService;
